/**
 * Workspace Slice - Core React Flow state management
 * Handles nodes, edges, and viewport operations
 */

import { applyNodeChanges, applyEdgeChanges, addEdge as addReactFlowEdge } from '@xyflow/react';
import type { StateCreator } from 'zustand';
import type { WorkflowStore, WorkspaceSlice, WorkflowNodeData, WorkflowDefinition } from './types';

const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 };

export const createWorkspaceSlice: StateCreator<WorkflowStore, [], [], WorkspaceSlice> = (set, get) => ({
  // Initial State
  nodes: [],
  edges: [],
  viewport: DEFAULT_VIEWPORT,

  // React Flow Change Handlers
  onNodesChange: (changes) => {
    set((state) => ({
      nodes: applyNodeChanges(changes, state.nodes)
    }));
    // Mark as dirty when nodes change
    get().markAsDirty();
  },

  onEdgesChange: (changes) => {
    set((state) => ({
      edges: applyEdgeChanges(changes, state.edges)
    }));
    // Mark as dirty when edges change
    get().markAsDirty();
  },

  onConnect: (connection) => {
    // Validate connection before adding
    const { source, target } = connection;
    if (!source || !target) return;

    // Prevent self-connections
    if (source === target) return;

    // Check for existing connection
    const existingEdge = get().edges.find((edge) => edge.source === source && edge.target === target);
    if (existingEdge) return;

    set((state) => ({
      edges: addReactFlowEdge(
        {
          ...connection,
          id: `e-${source}-${target}-${Date.now()}`,
          type: 'smoothstep'
        },
        state.edges
      )
    }));
    get().markAsDirty();
  },

  // Node Operations
  setNodes: (nodes) => {
    set({ nodes });
    get().markAsDirty();
  },

  setEdges: (edges) => {
    set({ edges });
    get().markAsDirty();
  },

  addNode: (node) => {
    set((state) => ({
      nodes: [...state.nodes, node]
    }));
    get().markAsDirty();
  },

  removeNode: (nodeId) => {
    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== nodeId),
      // Also remove connected edges
      edges: state.edges.filter((e) => e.source !== nodeId && e.target !== nodeId)
    }));

    // Clear selection if removed node was selected
    if (get().selectedNodeId === nodeId) {
      get().setSelectedNodeId(null);
    }
    get().markAsDirty();
  },

  updateNodeData: <T extends WorkflowNodeData>(nodeId: string, data: Partial<T>) => {
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                ...data,
                isConfigured: true // Mark as configured when data is updated
              }
            }
          : node
      )
    }));
    get().markAsDirty();
  },

  updateNodePosition: (nodeId, position) => {
    set((state) => ({
      nodes: state.nodes.map((node) => (node.id === nodeId ? { ...node, position } : node))
    }));
    // Don't mark as dirty for position changes (too frequent during drag)
  },

  // Edge Operations
  addEdge: (edge) => {
    set((state) => ({
      edges: [...state.edges, edge]
    }));
    get().markAsDirty();
  },

  removeEdge: (edgeId) => {
    set((state) => ({
      edges: state.edges.filter((e) => e.id !== edgeId)
    }));

    // Clear selection if removed edge was selected
    if (get().selectedEdgeId === edgeId) {
      get().setSelectedEdgeId(null);
    }
    get().markAsDirty();
  },

  // Viewport Operations
  setViewport: (viewport) => {
    set({ viewport });
  },

  // Bulk Operations
  clearWorkspace: () => {
    set({
      nodes: [],
      edges: [],
      viewport: DEFAULT_VIEWPORT
    });
    get().clearSelection();
    get().setCurrentWorkflowId(null);
    get().setCurrentWorkflowName('Untitled Workflow');
    get().setCurrentWorkflowDescription('');
    // Reset workflow metadata (display messages only)
    get().setWorkflowMetadata({
      welcomeMessage: '',
      errorMessage: ''
    });
    get().setIsDirty(false);
    get().clearValidationErrors();
  },

  loadWorkflow: (definition: WorkflowDefinition) => {
    set({
      nodes: definition.nodes,
      edges: definition.edges,
      viewport: definition.viewport || DEFAULT_VIEWPORT
    });
    get().setCurrentWorkflowName(definition.metadata.name);
    get().setCurrentWorkflowDescription(definition.metadata.description || '');
    // Load workflow metadata (display messages only - prompts are in nodes)
    get().setWorkflowMetadata({
      welcomeMessage: definition.metadata.welcomeMessage || '',
      errorMessage: definition.metadata.errorMessage || ''
    });
    get().setIsDirty(false);
    get().clearSelection();
    get().clearValidationErrors();
  }
});
